// src/components/ChromeExtensionUsage.js

import React from "react";
import { Link } from "react-router-dom";
import "./ChromeExtensionUsage.css";

export default function ChromeExtensionUsage() {
  return (
    <div className="extension-usage">
      <h1>Using the GrandmasterAI Chrome Extension</h1>

      <section>
        <h2>1. Prerequisites</h2>
        <ul>
          <li>
            A registered account on{" "}
            <a href="https://grandmasterai.com">grandmasterai.com</a>.
          </li>
          <li>
            A Lichess account (ideally using the same email you used above).
          </li>
          <li>
            Google Chrome (version 90+). The extension currently integrates with{" "}
            <strong>lichess.org</strong> games.
          </li>
          <li>
            Microphone permission enabled for the extension (you’ll be prompted
            on first use).
          </li>
        </ul>
      </section>

      <section>
        <h2>2. Installation</h2>
        <ol>
          <li>
            Visit the <Link to="/#hero">Home page</Link> and click “Download
            Chrome Extension” (Beta Mode), or grab it directly from the{" "}
            <a
              href="https://chromewebstore.google.com/detail/mldmjndlhapdfkbaeeeohdabhdkgngac?utm_source=item-share-cb"
              target="_blank"
              rel="noopener"
            >
              Chrome Web Store
            </a>
            .
          </li>
          <li>
            In the Chrome Web Store page that opens, click “Add to Chrome” and
            confirm.
          </li>
          <li>
            Once installed, the GrandmasterAI icon will appear in your toolbar.
          </li>
        </ol>
      </section>

      <section>
        <h2>3. First-Time Setup</h2>
        <ol>
          <li>
            Click the GrandmasterAI icon → toggle <strong>Online Mode</strong>{" "}
            or <strong>Offline Mode</strong> (online lets games sync back to
            your dashboard immediately).
          </li>
          <li>
            Click “Log in” in the popup to authorize your GrandmasterAI account.
          </li>
          <li>
            If you're a Plus user, choose your preferred Speech-to-Text engine.
            Free users default to the browser's built-in STT.
          </li>
          <li>
            Toggle “Save Full Game Audio” if you'd like to keep every blob in
            IndexedDB for offline playback.
          </li>
        </ol>
      </section>

      <section>
        <h2>4. Capturing Your Thoughts During Play</h2>
        <ol>
          <li>
            Navigate to{" "}
            <a href="https://lichess.org/" target="_blank" rel="noreferrer">
              lichess.org
            </a>{" "}
            and start a new live or correspondence game.
          </li>
          <li>
            Click the microphone icon injected into the Lichess UI—this begins
            recording your verbal analysis move by move.
          </li>
          <li>
            Speak out loud as you would at the board; our STT engine will
            transcribe in real time and annotate each move in the scoresheet.
          </li>
          <li>
            When the game ends, the extension stops automatically and—if in
            online mode—pushes your transcript & PGN back to your dashboard.
          </li>
        </ol>
      </section>

      <section>
        <h2>5. Reviewing & Exporting</h2>
        <ul>
          <li>
            On Lichess: click “Analysis” → you’ll see a “Play your voice back”
            button next to each move.
          </li>
          <li>
            In the bottom-left of the analysis pane, use “Share & export” to
            download:
            <ul>
              <li>Annotated PGN (with your spoken comments).</li>
              <li>Full audio ZIP (if you enabled full-game audio).</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>6. Managing Your Games</h2>
        <ol>
          <li>
            Go to your <Link to="/dashboard">GrandmasterAI Dashboard</Link>,
            then open “Synced Games / Transcripts.”
          </li>
          <li>
            For each game you can:
            <ul>
              <li>
                Set visibility: <em>Private</em>, <em>Public</em> or{" "}
                <em>Shared</em> (Plus only).
              </li>
              <li>Download the raw transcript or annotated PGN.</li>
              <li>Request a deeper “Master Analysis” (Plus users only).</li>
            </ul>
          </li>
        </ol>
      </section>

      <section>
        <h2>7. Advanced & Troubleshooting</h2>
        <ul>
          <li>
            <strong>Clear Local Data:</strong> In Offline Mode, click “Clear
            Local Game Data” in the popup to free up IndexedDB & localStorage.
          </li>
          <li>
            <strong>Storage Usage:</strong> See how much you're using under
            “Offline Storage Usage” in the popup.
          </li>
          <li>
            <strong>Permission Issues:</strong> If the mic icon never appears,
            make sure you've granted “Microphone” permission in
            chrome://extensions → GrandmasterAI → “Site access.”
          </li>
          <li>
            <strong>Support:</strong> Still stuck? Email{" "}
            <a href="mailto:grandmasterai.com@gmail.com">
              grandmasterai.com@gmail.com
            </a>{" "}
            or join us on{" "}
            <a
              href="https://discord.gg/ezp9Py2VKU"
              target="_blank"
              rel="noreferrer"
            >
              Discord
            </a>
            .
          </li>
        </ul>
      </section>
    </div>
  );
}
